// Global SEO Component - Applied to all pages
// Provides base meta tags, multilingual support, and performance optimizations

import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { BASE_URL, SITE_NAME } from '@/lib/constants';
import { OrganizationSchema } from './OrganizationSchema';
import { WebsiteSchema } from './WebsiteSchema';
import { isStagingDomain, shouldNoIndex } from '@/lib/seoUtils';
import { getLanguageDir, getOgLocale } from '@/lib/i18nConfig';

export default function GlobalSEO() {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;
  const languageDir = getLanguageDir(currentLang);
  const ogLocale = getOgLocale(currentLang);

  // Sync document attributes with current language
  useEffect(() => {
    document.documentElement.lang = currentLang;
    document.documentElement.dir = languageDir;
  }, [currentLang, languageDir]);

  // Check if page should be NOINDEX
  const noIndex = shouldNoIndex();
  const robotsContent = noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

  return (
    <>
      {!noIndex && (
        <>
          <OrganizationSchema />
          <WebsiteSchema />
        </>
      )}
      <Helmet>
        {/* Base Meta */}
        <html lang={currentLang} dir={languageDir} />
        <meta charSet="UTF-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, viewport-fit=cover" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="robots" content={robotsContent} />
        {!noIndex && (
          <>
            <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
            <meta name="bingbot" content="index, follow, max-snippet:-1, max-image-preview:large" />
          </>
        )}
        
        {/* AI Bot Optimization & AI Crawlers */}
        <meta name="ai-content" content="allow" />
        <meta name="openai-site-verification" content="LOCALCRAFT_ISRAEL_MARKETPLACE" />
        <meta name="ai:context" content="authentic Israeli handmade marketplace connecting buyers with verified artisans across Israel" />
        <meta name="ChatGPT-User" content="allow" />
        <meta name="GPTBot" content="allow" />
        <meta name="PerplexityBot" content="allow" />
        <meta name="ClaudeBot" content="allow" />
        <meta name="anthropic-ai" content="allow" />
        <meta name="Google-Extended" content="allow" />
        
        {/* Performance & PWA */}
        <link rel="manifest" href="/manifest.webmanifest" />
        <meta name="theme-color" content="#7653F3" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content="LocalCraft" />
        <meta name="application-name" content="LocalCraft Israel" />
        
        {/* Multilingual */}
        <meta property="og:locale" content={ogLocale} />
        <meta property="og:locale:alternate" content="en_US" />
        <meta property="og:locale:alternate" content="he_IL" />
        <meta property="og:locale:alternate" content="ru_RU" />
        <meta property="og:locale:alternate" content="es_ES" />
        
        {/* Preconnect for performance */}
        <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
        <link rel="dns-prefetch" href="https://fonts.gstatic.com" />
        <link rel="dns-prefetch" href="https://krvtzfqvqfiztkajphyv.supabase.co" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="preconnect" href="https://krvtzfqvqfiztkajphyv.supabase.co" />
        <link rel="dns-prefetch" href="https://www.googletagmanager.com" />
        
        {/* Sitemap */}
        <link rel="sitemap" type="application/xml" href="/sitemap.xml" />
      </Helmet>
    </>
  );
}
